import React from 'react';
import ProfileAvatar from './ProfileAvatar';
import { useProfileData } from './useProfileData';
import { useSignOut } from './useSignOut';

import { cn } from '@/lib/utils';
import { useTheme } from '@/hooks/layout/use-theme';
import { getUserInitials, getFullName, getMenuItems } from './utils';
import { UserProfileMenuProps } from './types';
import { LogOut, User, CreditCard, Activity, LayoutGrid, Cloud, Users, ListTodo, Bell, Headphones } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import MiniAIUsageCard from '@/components/subscription/MiniAIUsageCard';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { motion } from 'framer-motion';
import { useLocalStorageState } from '@/hooks/ai-agents/useLocalStorageState';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { useAssignedTasksCount } from '@/hooks/tasks/use-assigned-tasks-count';
import { useNotifications } from '@/contexts/notification-context';

const UserProfileMenu = ({ user }: UserProfileMenuProps) => {
  // Now uses centralized auth context through props (user passed down from parent)
  const { profileData } = useProfileData(user);
  const { handleSignOut } = useSignOut();
  const { theme } = useTheme();
  const { isModalOpen, closeModal } = useLocalStorageState();
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  
  // Mobile-only data for tasks and notifications
  const { data: assignedCount = 0 } = useAssignedTasksCount();
  const { unreadCount } = useNotifications();
  
  // Prepare data for display
  const fullName = getFullName(profileData);
  const initials = getUserInitials(profileData.firstName, profileData.lastName);

  // Handler to close chat modal when navigation links are clicked
  const handleLinkClick = () => {
    if (isModalOpen) {
      closeModal();
    }
  };

  // Mobile navigation handlers
  const handleTasksClick = () => {
    handleLinkClick();
    navigate('/task-manager');
  };

  const handleNotificationsClick = () => {
    handleLinkClick();
    navigate('/notifications');
  };

  const handleStorageClick = () => {
    handleLinkClick();
    navigate('/storage');
  };

  return (
    <div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div>
            <ProfileAvatar
              profilePictureUrl={profileData.profilePictureUrl}
              initials={initials}
              getFullName={() => fullName}
            />
          </div>
        </DropdownMenuTrigger>
      
              <DropdownMenuContent 
                align="end" 
                className="w-64 p-0 border border-accent/20 shadow-lg backdrop-blur-xl 
                  bg-white/95 dark:bg-black/90 rounded-xl overflow-hidden"
              >
                {/* Mini AI Usage Card - moved to top */}
                <div className="px-2 py-3 border-b border-slate-100 dark:border-slate-800/50">
                  <MiniAIUsageCard />
                </div>
                
                {/* Mobile-only menu items */}
                {isMobile && (
                  <>
                    <div className="p-2">
                      <DropdownMenuItem asChild className="hover:bg-transparent focus:bg-transparent">
                        <button
                          onClick={handleTasksClick}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium w-full transition-all duration-200
                            text-slate-700 dark:text-slate-200 hover:text-white
                            hover:bg-gradient-to-r hover:from-brandPurple hover:to-brandBlue
                            group relative"
                        >
                          <ListTodo size={16} className="text-slate-500 dark:text-slate-400 group-hover:text-white" />
                          Tasks
                          {assignedCount > 0 && (
                            <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-semibold bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full">
                              {assignedCount > 99 ? '99+' : assignedCount}
                            </span>
                          )}
                        </button>
                      </DropdownMenuItem>

                      <DropdownMenuItem asChild className="hover:bg-transparent focus:bg-transparent">
                        <button
                          onClick={handleNotificationsClick}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium w-full transition-all duration-200
                            text-slate-700 dark:text-slate-200 hover:text-white
                            hover:bg-gradient-to-r hover:from-brandPurple hover:to-brandBlue
                            group relative"
                        >
                          <Bell size={16} className="text-slate-500 dark:text-slate-400 group-hover:text-white" />
                          Notifications
                          {unreadCount > 0 && (
                            <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-semibold bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full">
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          )}
                        </button>
                      </DropdownMenuItem>

                      <DropdownMenuItem asChild className="hover:bg-transparent focus:bg-transparent">
                        <button
                          onClick={handleStorageClick}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium w-full transition-all duration-200
                            text-slate-700 dark:text-slate-200 hover:text-white
                            hover:bg-gradient-to-r hover:from-brandPurple hover:to-brandBlue
                            group"
                        >
                          <Cloud size={16} className="text-slate-500 dark:text-slate-400 group-hover:text-white" />
                          Storage
                        </button>
                      </DropdownMenuItem>
                    </div>
                    
                    <DropdownMenuSeparator className="mx-2 my-1 bg-slate-200/50 dark:bg-slate-700/50" />
                  </>
                )}
                
                <div className="p-2">
                  <DropdownMenuItem asChild className="hover:bg-transparent focus:bg-transparent">
                    <Link
                      id="account-link"
                      to="/account"
                      onClick={handleLinkClick}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium w-full transition-all duration-200
                        text-slate-700 dark:text-slate-200 hover:text-white
                        hover:bg-gradient-to-r hover:from-brandPurple hover:to-brandBlue
                        group"
                    >
                      <User size={16} className="text-slate-500 dark:text-slate-400 group-hover:text-white" />
                      Account
                    </Link>
                  </DropdownMenuItem>

                  <DropdownMenuItem asChild className="hover:bg-transparent focus:bg-transparent">
                    <Link
                      to="/app-library"
                      onClick={handleLinkClick}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium w-full transition-all duration-200
                        text-slate-700 dark:text-slate-200 hover:text-white
                        hover:bg-gradient-to-r hover:from-brandPurple hover:to-brandBlue
                        group"
                    >
                      <LayoutGrid size={16} className="text-slate-500 dark:text-slate-400 group-hover:text-white" />
                      App Center
                    </Link>
                  </DropdownMenuItem>
                </div>
                
                <DropdownMenuSeparator className="mx-2 my-1 bg-slate-200/50 dark:bg-slate-700/50" />
                
                <div className="p-2">
                  <DropdownMenuItem asChild className="hover:bg-transparent focus:bg-transparent">
                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      onClick={handleSignOut}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium w-full transition-all duration-200
                        text-rose-600 dark:text-rose-400 hover:text-white
                        hover:bg-gradient-to-r hover:from-rose-500 hover:to-red-600
                        group"
                    >
                      <LogOut size={16} className="text-rose-500 dark:text-rose-400 group-hover:text-white" />
                      Sign Out
                    </motion.button>
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
  );
};

export default UserProfileMenu;
